import React from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { tr } from '../../lib/i18nHelpers';
import { TableControls } from './useTableControls';

export const PAGE_SIZE_OPTIONS = [20, 50, 100];

/** Table footer: page-size selector, prev/next arrows, counts, clear-filters,
 *  and the truncation banner when the server total exceeds the loaded rows. */
export function TablePager({ controls }: { controls: TableControls }) {
  const { page, pageCount, pageSize, filteredCount, totalCount, loadedCount, truncated, activeFilterCount } = controls;

  const counts = filteredCount === totalCount
    ? tr(`${totalCount} rows`, `共 ${totalCount} 筆`, `共 ${totalCount} 笔`)
    : tr(`${filteredCount} of ${totalCount} rows`, `共 ${filteredCount} / ${totalCount} 筆`, `共 ${filteredCount} / ${totalCount} 笔`);

  return (
    <>
      {truncated && (
        <div data-testid="table-truncated"
             className="px-3 py-2 text-xs bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300 border-t">
          {tr(
            `Showing first ${loadedCount} of ${totalCount} — use search to narrow`,
            `已載入前 ${loadedCount} 筆，共 ${totalCount} 筆 — 請用搜尋縮小範圍`,
            `已载入前 ${loadedCount} 笔，共 ${totalCount} 笔 — 请用搜寻缩小范围`,
          )}
        </div>
      )}
      <div data-testid="table-pager" className="p-3 flex flex-wrap items-center justify-between gap-2 text-sm border-t">
        <div className="flex items-center gap-2">
          <select data-testid="pager-size" aria-label={tr('Rows per page', '每頁顯示', '每页显示')}
                  value={pageSize}
                  onChange={e => controls.setPageSize(Number(e.target.value))}
                  className="px-2 py-1 border rounded-md bg-background text-sm">
            {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className="text-muted-foreground">{tr('rows / page', '每頁', '每页')}</span>
          {activeFilterCount > 0 && (
            <button type="button" data-testid="pager-clear-filters" onClick={controls.clearAllFilters}
                    className="flex items-center gap-1 px-2 py-1 border rounded-md text-xs hover:bg-muted">
              <X className="h-3 w-3" />
              {tr('Clear filters', '清除篩選', '清除筛选')} ({activeFilterCount})
            </button>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="pager-counts" className="text-muted-foreground">{counts}</span>
          <span data-testid="pager-page" className="text-muted-foreground">
            {tr(`Page ${page} / ${pageCount}`, `第 ${page} / ${pageCount} 頁`, `第 ${page} / ${pageCount} 页`)}
          </span>
          <button type="button" data-testid="pager-prev" disabled={page <= 1}
                  onClick={() => controls.setPage(page - 1)}
                  aria-label={tr('Previous page', '上一頁', '上一页')}
                  className="px-3 py-1 border rounded-md disabled:opacity-50">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button type="button" data-testid="pager-next" disabled={page >= pageCount}
                  onClick={() => controls.setPage(page + 1)}
                  aria-label={tr('Next page', '下一頁', '下一页')}
                  className="px-3 py-1 border rounded-md disabled:opacity-50">
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </>
  );
}
